import './WBAgentThinkingMark.css';

import {Icon, type IconProps} from '@wandb/components';
import {clsx} from 'clsx';
import React, {useState} from 'react';

/**
 * WBAgentThinkingMark shows the ARIA logo and morphs it into the standard
 * spinner while the agent works. Keep it mounted and toggle `isActive` so the
 * transition plays both ways: the star collapses into a spinning ring of ticks
 * when work starts and the ring collapses back into the star when it settles.
 *
 * The star turns a quarter turn clockwise on every state change and never
 * unwinds. The logo is symmetric under a half turn, so every idle state (an
 * even number of quarter turns) looks like the logo, and the star is hidden
 * at the odd ones. Turning back the way it came would run against the ring's
 * spin direction.
 *
 * The mark inherits `currentColor`, so wrap it in the accent color you want
 * (`text-wb-accent`). It is decorative; the host owns the accessible label.
 */
export const WBAgentThinkingMark = ({
  isActive,
  size = 'md',
  className,
}: {
  isActive: boolean;
  size?: IconProps['size'];
  className?: string;
}) => {
  // Count state changes so the star's rotation only ever increases. Storing
  // the previous prop in state and updating during render is the React
  // pattern for deriving state from a prop change.
  const [turns, setTurns] = useState({isActive, count: 0});
  if (turns.isActive !== isActive) {
    setTurns({isActive, count: turns.count + 1});
  }

  return (
    <span
      aria-hidden
      data-active={isActive || undefined}
      style={
        {
          '--wbagent-thinking-mark-turn': `${turns.count * 90}deg`,
        } as React.CSSProperties
      }
      className={clsx('wbagent-thinking-mark', className)}>
      <span className="wbagent-thinking-mark-star">
        <Icon icon="lightboard-solid" size={size} color="currentcolor" />
      </span>
      <span className="wbagent-thinking-mark-ring">
        <span className="wbagent-thinking-mark-ring-spin">
          <Icon icon="loading" size={size} color="currentcolor" />
        </span>
      </span>
    </span>
  );
};
